import React, { useEffect, useRef, useState } from 'react'
import { IconButton, Paper, Slider, Stack, Typography } from '@mui/material'

import { Icon } from '~components/internal'

interface AudioPlayerProps {
  audioUrl: string
  onError?: (error: Error) => void
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl, onError }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const animationRef = useRef<number | null>(null)
  const isSeekingRef = useRef(false)

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isMuted, setIsMuted] = useState(false)

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateTime = (): void => {
      if (!isSeekingRef.current) {
        const newTime = audio.currentTime
        setCurrentTime(newTime)
      }

      if (isPlaying) {
        animationRef.current = requestAnimationFrame(updateTime)
      }
    }

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateTime)
    }

    return (): void => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isPlaying])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleLoadedMetadata = (): void => {
      setDuration(audio.duration)
    }

    const handleEnded = (): void => {
      setIsPlaying(false)
      setCurrentTime(0)
    }

    const handleError = (e: Event): void => {
      console.error('Audio playback error:', e)
      if (onError) {
        onError(new Error('Audio playback failed'))
      }
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)

    return (): void => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
    }
  }, [onError])

  const togglePlayPause = (): void => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio.play()
      setIsPlaying(true)
    }
  }

  const handleSliderChangeCommitted = (
    _event: React.SyntheticEvent | Event,
    value: number | number[],
  ): void => {
    const audio = audioRef.current
    if (!audio) return

    isSeekingRef.current = false
    const newTime = value as number
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }

  const handleSliderChange = (_event: Event, value: number | number[]): void => {
    isSeekingRef.current = true
    setCurrentTime(value as number)
  }

  const toggleMute = (): void => {
    const audio = audioRef.current
    if (!audio) return

    audio.muted = !isMuted
    setIsMuted(!isMuted)
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: '#f5f5f5',
        borderRadius: 2,
        border: '1px solid #e0e0e0',
      }}
    >
      <audio ref={audioRef} src={audioUrl} preload='metadata' />
      <Stack spacing={1.5}>
        {/* Controls */}
        <Stack
          direction='row'
          spacing={2}
          sx={{
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={togglePlayPause}
            size='small'
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
              },
            }}
          >
            {isPlaying ? <Icon name='pause' /> : <Icon name='play' />}
          </IconButton>

          <Typography
            variant='caption'
            sx={{
              color: 'text.secondary',
              minWidth: 40,
            }}
          >
            {formatTime(currentTime)}
          </Typography>

          <Slider
            value={currentTime}
            max={duration || 100}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            size='small'
            sx={{
              flexGrow: 1,
              '& .MuiSlider-thumb': {
                width: 12,
                height: 12,
                transition: 'none',
              },
              '& .MuiSlider-track': {
                transition: 'none',
              },
              '& .MuiSlider-rail': {
                transition: 'none',
              },
            }}
          />

          <Typography
            variant='caption'
            sx={{
              color: 'text.secondary',
              minWidth: 40,
            }}
          >
            {formatTime(duration)}
          </Typography>

          <IconButton onClick={toggleMute} size='small'>
            {isMuted ? (
              <Icon name='mute' fontSize='small' />
            ) : (
              <Icon name='volume' fontSize='small' />
            )}
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default AudioPlayer
